import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Theme } from 'baseui';
import { useStyletron } from 'baseui';
import { CANVAS } from '../../../constants/template-builder';
import { CoreStageProvider } from '../../../providers/core-stage-provider/core-stage-provider';
import type { StageSettingsT } from '../../../providers/core-stage-provider/types';
import { CanvasToolbar } from './canvas-toolbar';
import { DrawingSurface } from './drawing-surface';
import { ReferenceImageControls } from './reference-image-controls';
import { useCanvasSize } from './use-canvas-size';
import { useReferenceImage } from './use-reference-image';
type TemplateCanvasPropsT = {
  children: React.ReactNode;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onCanvasReady?: (dimensions: [number, number]) => void;
};
const getContainerStyles = (theme: Theme) => ({
  display: ('flex' as const),
  flex: 1,
  minWidth: '0',
  minHeight: '0',
  padding: theme.sizing.scale600
});
const getStageWrapperStyles = (theme: Theme) => ({
  display: ('flex' as const),
  flex: 1,
  minWidth: '0',
  borderRadius: theme.borders.radius300,
  overflow: ('hidden' as const)
});
export function TemplateCanvas({
  children,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onCanvasReady
}: TemplateCanvasPropsT) {
  const [css, theme] = useStyletron();
  const [stageSettings, setStageSettings] = useState<StageSettingsT>(CANVAS.DEFAULT_STAGE_SETTINGS);
  const hasMountedRef = useRef(false);
  const hasNotifiedRef = useRef(false);
  const {
    wrapperRef,
    canvasSize
  } = useCanvasSize();
  const {
    backgroundImage,
    imageOpacity,
    setImageOpacity,
    handleImageSelect,
    handleRemoveImage
  } = useReferenceImage();
  useEffect(() => {
    if (canvasSize && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      onCanvasReady?.([canvasSize.width, canvasSize.height]);
    }
  }, [canvasSize, onCanvasReady]);
  const handleMount = useCallback((fitToScreen: () => void) => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;
    fitToScreen();
  }, []);
  return <div className={css(getContainerStyles(theme))} data-testid="template-canvas">
      <div className={css(getStageWrapperStyles(theme))} ref={wrapperRef}>
        {canvasSize && <CoreStageProvider canvasSize={canvasSize} stageSettings={stageSettings} setLocalStageSettings={setStageSettings} isDraggable={false} suppressPan onMount={handleMount} renderOverlay={() => <>
                <CanvasToolbar onUndo={onUndo} onRedo={onRedo} canUndo={canUndo} canRedo={canRedo} />
                <ReferenceImageControls backgroundImage={backgroundImage} imageOpacity={imageOpacity} onImageSelect={handleImageSelect} onOpacityChange={setImageOpacity} onRemoveImage={handleRemoveImage} />
              </>}>
            <DrawingSurface backgroundImage={backgroundImage} imageOpacity={imageOpacity} />
            {children}
          </CoreStageProvider>}
      </div>
    </div>;
}